// FloatingDictation.jsx — one floating mic for the Cart's NOTE fields
// (Brian, 2026-08-24). Returns/credit notes get dictated constantly in the
// field, but a mic chip on every input is clutter — so ONE pill floats
// bottom-right (follows scroll) and types into whichever note box was last
// tapped (order note / line note / credit text — the page decides via
// onText). Shares the 한/EN recognition-language choice with the search
// mics (same localStorage key). Hidden where speech is unsupported.
import { useEffect, useRef, useState } from "react";
import { useLang } from "../i18n/LanguageContext";

const SR = typeof window !== "undefined"
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

const MIC_LANG_KEY = "sunjin-mic-lang"; // persists the 한/EN choice per device

export default function FloatingDictation({ onText }) {
  const { lang } = useLang();
  const [listening, setListening] = useState(false);
  const [micLang, setMicLang] = useState(() => {
    try { return localStorage.getItem(MIC_LANG_KEY) || null; } catch { return null; }
  });
  const recRef = useRef(null);

  // Ride ABOVE the phone keyboard (Brian, 2026-08-24): when the on-screen
  // keyboard opens it covers fixed-bottom elements — visualViewport tells us
  // how much of the window it eats, and the pill shifts up by that amount.
  const [kbOffset, setKbOffset] = useState(0);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const covered = window.innerHeight - (vv.height + vv.offsetTop);
      setKbOffset(covered > 0 ? Math.round(covered) : 0);
    };
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    update();
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);

  useEffect(() => () => { recRef.current?.abort?.(); }, []);

  if (!SR) return null;

  const effLang = micLang || lang;

  function toggleMicLang() {
    const next = effLang === "ko" ? "en" : "ko";
    setMicLang(next);
    try { localStorage.setItem(MIC_LANG_KEY, next); } catch { /* private mode */ }
  }

  function start() {
    if (listening) { recRef.current?.stop?.(); return; }
    const rec = new SR();
    recRef.current = rec;
    rec.lang = effLang === "ko" ? "ko-KR" : "en-CA";
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      const text = e.results?.[0]?.[0]?.transcript || "";
      if (text) onText(text);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    setListening(true);
    try { rec.start(); } catch { setListening(false); }
  }

  return (
    <div className="dictation-fab" style={kbOffset ? { bottom: 12 + kbOffset } : undefined}>
      <button
        type="button"
        className={`mic-btn dictation-mic${listening ? " mic-listening" : ""}`}
        onMouseDown={(e) => e.preventDefault()} /* keep the note field focused */
        onClick={start}
        title={effLang === "ko"
          ? "마지막에 누른 메모칸에 말로 입력"
          : "Dictate into the note box you last tapped"}
      >
        {listening ? "🎙" : "🎤"}
      </button>
      <button
        type="button"
        className="mic-lang-chip"
        onMouseDown={(e) => e.preventDefault()}
        onClick={toggleMicLang}
        title={effLang === "ko" ? "지금: 한국어 — 탭하면 영어" : "Now: English — tap for Korean"}
      >
        {effLang === "ko" ? "한" : "EN"}
      </button>
    </div>
  );
}
